import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { updateEnrollmentFees } from "@/services/ficheService";
import type { LoadedFiche } from "@/services/ficheService";
import type { EnrollmentFees } from "@/types/fiche";
import { currencySymbol } from "@/utils/currency";

interface Props {
  open: boolean;
  onClose: () => void;
  fiche: LoadedFiche;
  onSaved: () => void;
}

export default function EnrollmentFeesModal({ open, onClose, fiche, onSaved }: Props) {
  const { notify } = useToast();
  const fees = fiche.enrollmentFees;
  const [busy, setBusy] = useState(false);
  const [inscription, setInscription] = useState("0");
  const [reinscription, setReinscription] = useState("0");

  useEffect(() => {
    if (!open) return;
    setInscription(String(fees.inscription));
    setReinscription(String(fees.reinscription));
  }, [open, fees]);

  const handleSave = async () => {
    const next: EnrollmentFees = {
      inscription: Number(inscription) || 0,
      reinscription: Number(reinscription) || 0,
    };
    setBusy(true);
    try {
      await updateEnrollmentFees(fiche, next);
      notify("Frais d'inscription / réinscription mis à jour.");
      onSaved();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Frais d'inscription & réinscription"
      subtitle={`Montants uniques pour ${fiche.header.schoolYear} — valables pour toutes les classes`}
      icon="ri-hand-coin-line"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy}>
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label={`Frais d'inscription (${currencySymbol()})`}
          type="number"
          min={0}
          value={inscription}
          hint="Pour un nouvel élève."
          onChange={(e) => setInscription(e.target.value)}
        />
        <Input
          label={`Frais de réinscription (${currencySymbol()})`}
          type="number"
          min={0}
          value={reinscription}
          hint="Pour un élève qui se réinscrit."
          onChange={(e) => setReinscription(e.target.value)}
        />
        <p className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100/60 p-3 text-xs text-foreground-600">
          <i className="ri-information-line mt-0.5 text-sm text-secondary-700"></i>
          Ces deux montants s'appliquent à l'identique pour le primaire et le secondaire.
        </p>
      </div>
    </Modal>
  );
}